import React, { useState, useRef } from "react";
import { FileText, Sparkles } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../hooks/useReveal";
import { FOOD_MENU, COCKTAIL_MENU, BOTTLE_MENU, HOOKAH_MENU, HAPPY_HOUR, MENU_PDF, IMAGES, BRAND } from "../mock";

const TABS = ["Food", "Cocktails", "Bottle Service", "Hookah", "Happy Hour"];

function MenuItem({ name, desc, price, note }) {
  return (
    <div className="flex justify-between items-baseline gap-4 py-3 border-b border-white/8 group">
      <div className="min-w-0">
        <h4 className="font-display text-base md:text-lg text-nala-cream uppercase tracking-wide-lux group-hover:text-nala-gold transition-colors">
          {name} {note && <span className="text-nala-gold text-sm normal-case tracking-normal">· {note}</span>}
        </h4>
        {desc && <p className="text-nala-cream-muted text-sm font-serif-lux text-base mt-1 leading-snug">{desc}</p>}
      </div>
      {price && <span className="text-nala-gold font-medium shrink-0 whitespace-nowrap">{price}</span>}
    </div>
  );
}

function CategoryBlock({ title, note, children }) {
  return (
    <Reveal className="mb-12">
      <div className="flex items-center gap-4 mb-5">
        <h3 className="font-display text-xl md:text-2xl text-gold-gradient uppercase tracking-wide-lux">{title}</h3>
        {note && <span className="text-nala-gold text-sm border border-nala-gold/40 px-3 py-0.5">{note}</span>}
        <div className="flex-1 h-px bg-gradient-to-r from-nala-gold/40 to-transparent" />
      </div>
      {children}
    </Reveal>
  );
}

export default function Menu() {
  const [tab, setTab] = useState("Food");
  const tabsRef = useRef(null);

  const handleTab = (t) => {
    setTab(t);
    // Scroll so the tab bar sits just under the fixed navbar
    if (tabsRef.current) {
      const y = tabsRef.current.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className="pt-28 pb-24 bg-nala-black min-h-screen">
      {/* Header banner */}
      <div className="relative h-64 md:h-80 overflow-hidden mb-16">
        <img src={IMAGES.lobster2} alt="Nala menu" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-nala-black via-nala-black/60 to-nala-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-nala-gold tracking-luxe uppercase text-sm mb-3">Executive Chef · {BRAND.chef}</p>
          <h1 className="font-display text-5xl md:text-7xl text-gold-gradient">The Menu</h1>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <Reveal className="text-center mb-10">
          <p className="font-serif-lux text-xl text-nala-cream-muted leading-relaxed max-w-2xl mx-auto">
            In addition to the items below, we offer daily specials made with fresh seasonal
            ingredients. Be sure to ask your server about our daily specials.
          </p>
          <a href={MENU_PDF} target="_blank" rel="noreferrer" className="btn-outline-gold mt-8 inline-flex">
            <FileText size={16} /> <span>View Full Menu (PDF)</span>
          </a>
        </Reveal>

        {/* Tabs */}
        <div ref={tabsRef} className="flex flex-wrap justify-center gap-2 md:gap-3 mb-16 sticky top-20 z-20 bg-nala-black/90 backdrop-blur-md py-4">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => handleTab(t)}
              className={`px-5 py-2.5 text-xs md:text-sm uppercase tracking-[0.18em] border transition-all duration-300 ${
                tab === t
                  ? "bg-nala-gold text-nala-black border-nala-gold"
                  : "text-nala-cream border-nala-gold/30 hover:border-nala-gold hover:text-nala-gold"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Food */}
        {tab === "Food" && (
          <div>
            {FOOD_MENU.map((cat) => (
              <CategoryBlock key={cat.category} title={cat.category}>
                <div className="grid md:grid-cols-2 gap-x-12">
                  {cat.items.map((it) => <MenuItem key={it.name} {...it} />)}
                </div>
              </CategoryBlock>
            ))}
          </div>
        )}

        {/* Cocktails */}
        {tab === "Cocktails" && (
          <div>
            {COCKTAIL_MENU.map((cat) => (
              <CategoryBlock key={cat.category} title={cat.category} note={cat.note}>
                <div className="grid md:grid-cols-2 gap-x-12">
                  {cat.items.map((it) => <MenuItem key={it.name} {...it} />)}
                </div>
              </CategoryBlock>
            ))}
          </div>
        )}

        {/* Bottle */}
        {tab === "Bottle Service" && (
          <div>
            {BOTTLE_MENU.map((cat) => (
              <CategoryBlock key={cat.category} title={cat.category}>
                <div className="grid md:grid-cols-2 gap-x-12">
                  {cat.items.map((it) => <MenuItem key={it.name} {...it} />)}
                </div>
              </CategoryBlock>
            ))}
          </div>
        )}

        {/* Hookah */}
        {tab === "Hookah" && (
          <CategoryBlock title="Premium Hookah" note={HOOKAH_MENU.price}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12">
              {HOOKAH_MENU.flavors.map((f) => <MenuItem key={f} name={f} />)}
            </div>
            <p className="text-nala-cream-muted mt-6 font-serif-lux text-lg">Refill · <span className="text-nala-gold">{HOOKAH_MENU.refill}</span></p>
          </CategoryBlock>
        )}

        {/* Happy Hour */}
        {tab === "Happy Hour" && (
          <Reveal>
            <div className="text-center mb-10">
              <Sparkles className="mx-auto text-nala-gold mb-4" size={28} />
              <p className="text-nala-gold tracking-luxe uppercase">{HAPPY_HOUR.days} · {HAPPY_HOUR.time}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="border border-nala-gold/20 p-8 bg-nala-charcoal/50">
                <h3 className="font-display text-xl text-nala-gold uppercase tracking-wide-lux mb-5">Half Off Bites</h3>
                {HAPPY_HOUR.halfOff.map((f) => <MenuItem key={f} name={f} />)}
              </div>
              <div className="border border-nala-gold/20 p-8 bg-nala-charcoal/50">
                <h3 className="font-display text-xl text-nala-gold uppercase tracking-wide-lux mb-5">Glasses</h3>
                {HAPPY_HOUR.glasses.map((g) => <MenuItem key={g.name} {...g} />)}
              </div>
              <div className="border border-nala-gold/20 p-8 bg-nala-charcoal/50">
                <h3 className="font-display text-xl text-nala-gold uppercase tracking-wide-lux mb-5">Shots & Cocktails</h3>
                {HAPPY_HOUR.shots.map((s) => <MenuItem key={s.name} {...s} />)}
              </div>
            </div>
          </Reveal>
        )}

        {/* Disclaimer */}
        <p className="text-center text-nala-cream-muted/70 text-xs mt-16 max-w-2xl mx-auto leading-relaxed tracking-wide-lux">
          *Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs may increase your
          risk of foodborne illness. Please drink responsibly. Be prepared to show I.D. — no alcohol
          served to patrons under 21.
        </p>
      </div>
    </div>
  );
}
